import { useEffect } from 'react';
import * as yup from 'yup';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { fileShapeNative, fileShapeWeb } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import { PageCode } from '@gov-nx/module/page';
import { useDataBoxStore } from '@gov-nx/store';
import { useAutocompleteDefinition } from '@gov-nx/utils/common';
import type { DataBoxFolderType } from '../providers/DataBoxes.types';
import type {
	DataBoxMessageFormInputs,
	DataboxMessageFormReactionsType,
} from '../providers/MessageFormContext.types';
import { useMessageDetail } from './useMessageDetail';

export const useNewMessageForm = (
	dataBoxId?: string,
	folderType?: DataBoxFolderType,
	messageId?: number,
	reaction?: DataboxMessageFormReactionsType,
	isWeb = false
) => {
	const { t } = useLocale(PageCode['datove-schranky']);
	const lastOpenedDataBoxId = useDataBoxStore('lastOpenedDatabox');
	const setDataboxFormDirty = useDataBoxStore('setIsDataboxFormDirty');

	const {
		message,
		isMessageLoading,
		isDataBoxLoading,
		senderDataBox,
		recipientDataBox,
	} = useMessageDetail({
		dataBoxId,
		folderType,
		messageId,
	});

	const recipientAutocomplete = useAutocompleteDefinition({
		name: 'recipientAutocomplete',
		label: t('nova-zprava.komu'),
		placeholder: t('nova-zprava.vyhledejte'),
		requiredMessage: t('nova-zprava.chyba.prijemce'),
	});

	const formSchemaNative = yup
		.object({
			senderId: yup.string().required(t('nova-zprava.chyba.odesilatel')),
			recipientAutocomplete: recipientAutocomplete.schema,
			subject: yup.string().required(t('nova-zprava.chyba.predmet')),
			message: yup
				.string()
				.max(5000, t('nova-zprava.chyba.zprava-delka'))
				.test(
					'test empty string',
					t('nova-zprava.chyba.zprava-zadny-text'),
					(value) => {
						const contentLength = value?.length || 0;
						const trimmedLength = value?.trim().length || 0;

						return (
							(contentLength > 0 && trimmedLength > 0) || contentLength === 0
						);
					}
				)
				.when(['documents', 'files'], {
					is: (documents: number[], files: string[]) => {
						// TODO SMID: Default values for documents and files are undefined, so we need to check if they are arrays
						if (Array.isArray(documents) && Array.isArray(files)) {
							return documents.length === 0 && files.length === 0;
						}
						return false;
					},
					then: (schema) => schema.required(t('nova-zprava.chyba.zprava')),
				}),
			toTheHandsOf: yup.string(),
			intoTheirOwnHands: yup.boolean(),
			senderIdentification: yup.boolean(),
			naseCisloJednaci: yup.string(),
			naseSpisovaZnacka: yup.string(),
			vaseCisloJednaci: yup.string(),
			vaseSpisovaZnacka: yup.string(),
			documents: yup.array().of(yup.number().required()),
			files: yup.array().of(fileShapeNative()).required(),
			temporary: yup.object({
				additionalData: yup.object({
					toTheHandsOf: yup.string(),
					intoTheirOwnHands: yup.boolean(),
					senderIdentification: yup.boolean(),
					naseCisloJednaci: yup.string(),
					naseSpisovaZnacka: yup.string(),
					vaseCisloJednaci: yup.string(),
					vaseSpisovaZnacka: yup.string(),
				}),
				files: yup.array().of(fileShapeNative()).required(),
			}),
		})
		.required();

	const formSchemaWeb = yup
		.object({
			senderId: yup.string().required(t('nova-zprava.chyba.odesilatel')),
			recipientAutocomplete: recipientAutocomplete.schema,
			subject: yup.string().required(t('nova-zprava.chyba.predmet')),
			message: yup
				.string()
				.max(5000, t('nova-zprava.chyba.zprava-delka'))
				.test(
					'test empty string',
					t('nova-zprava.chyba.zprava-zadny-text'),
					(value) => {
						const contentLength = value?.length || 0;
						const trimmedLength = value?.trim().length || 0;

						return (
							(contentLength > 0 && trimmedLength > 0) || contentLength === 0
						);
					}
				)
				.when(['documents', 'files'], {
					is: (documents: number[], files: string[]) => {
						// TODO SMID: Default values for documents and files are undefined, so we need to check if they are arrays
						if (Array.isArray(documents) && Array.isArray(files)) {
							return documents.length === 0 && files.length === 0;
						}
						return false;
					},
					then: (schema) => schema.required(t('nova-zprava.chyba.zprava')),
				}),
			toTheHandsOf: yup.string(),
			intoTheirOwnHands: yup.boolean(),
			senderIdentification: yup.boolean(),
			naseCisloJednaci: yup.string(),
			naseSpisovaZnacka: yup.string(),
			vaseCisloJednaci: yup.string(),
			vaseSpisovaZnacka: yup.string(),
			documents: yup.array().of(yup.number().required()),
			files: yup.array().of(fileShapeWeb()).required(),
			temporary: yup.object({
				additionalData: yup.object({
					toTheHandsOf: yup.string(),
					intoTheirOwnHands: yup.boolean(),
					senderIdentification: yup.boolean(),
					naseCisloJednaci: yup.string(),
					naseSpisovaZnacka: yup.string(),
					vaseCisloJednaci: yup.string(),
					vaseSpisovaZnacka: yup.string(),
				}),
				files: yup.array().of(fileShapeWeb()).required(),
			}),
		})
		.required();

	const createSubject = () => {
		if (message) {
			if (reaction === 'odpoved') {
				return `Re: ${message.vec}`;
			}
			if (reaction === 'dalsi') {
				return message.vec;
			}
		}
		return undefined;
	};

	const createRecipient = () => {
		if (reaction === 'odpoved' && senderDataBox) {
			return {
				value: '',
				selected: senderDataBox,
			};
		}
		if (reaction === 'dalsi' && recipientDataBox) {
			return {
				value: '',
				selected: recipientDataBox,
			};
		}
	};

	const defaultValues: DataBoxMessageFormInputs = {
		message: undefined,
		subject: createSubject(),
		senderId: lastOpenedDataBoxId,
		recipientAutocomplete: createRecipient(),
		naseCisloJednaci: message?.naseCisloJednaci,
		naseSpisovaZnacka: message?.naseSpisovaZnacka,
		vaseCisloJednaci: message?.vaseCisloJednaci,
		vaseSpisovaZnacka: message?.vaseSpisovaZnacka,
		files: [],
		documents: [],
		temporary: {
			additionalData: {},
			files: [],
		},
	};

	const formMethods = useGovForm<DataBoxMessageFormInputs>({
		mode: 'onTouched',
		reValidateMode: 'onBlur',
		formSchema: isWeb ? formSchemaWeb : formSchemaNative,
		defaultValues,
	});

	const formDefinition = getFormDefinition({
		formMethods,
		formSchema: isWeb ? formSchemaWeb : formSchemaNative,
	});

	useEffect(() => {
		if (!message || !senderDataBox || !recipientDataBox) {
			return;
		}

		formDefinition.formMethods.setValue('subject', createSubject());
		formDefinition.formMethods.setValue(
			'recipientAutocomplete',
			createRecipient()
		);
		formDefinition.formMethods.setValue(
			'naseCisloJednaci',
			message.naseCisloJednaci
		);
		formDefinition.formMethods.setValue(
			'naseSpisovaZnacka',
			message.naseSpisovaZnacka
		);
		formDefinition.formMethods.setValue(
			'vaseCisloJednaci',
			message.vaseCisloJednaci
		);
		formDefinition.formMethods.setValue(
			'vaseSpisovaZnacka',
			message.vaseSpisovaZnacka
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [message, senderDataBox, recipientDataBox]);

	useEffect(() => {
		if (formMethods.formState.isDirty) {
			setDataboxFormDirty(true);
		}

		return () => {
			setDataboxFormDirty(false);
		};
	}, [formMethods.formState.isDirty, setDataboxFormDirty]);

	return {
		form: formMethods,
		formSchema: formDefinition.formSchema,
		formDefinition,
		isFormDataLoading: isMessageLoading || isDataBoxLoading,
	};
};

export const prepareNewMessageSubmitData = (data: DataBoxMessageFormInputs) => {
	const requestBody = new FormData();

	if (data.senderId) {
		requestBody.append('odesilatelId', data.senderId);
	}
	if (data.recipientAutocomplete?.selected?.datovaSchrankaId) {
		requestBody.append(
			'prijemceId',
			data.recipientAutocomplete.selected.datovaSchrankaId
		);
	}
	if (data.subject) {
		requestBody.append('vec', data.subject);
	}

	if (data.message) {
		requestBody.append('text', data.message);
	}

	if (data.toTheHandsOf) {
		requestBody.append('kRukam', data.toTheHandsOf);
	}

	if (data.intoTheirOwnHands) {
		requestBody.append('doVlastnichRukou', data.intoTheirOwnHands.toString());
	}

	if (data.senderIdentification) {
		requestBody.append(
			'pridatIdentifikaciOdesilatele',
			data.senderIdentification.toString()
		);
	}

	if (data.naseCisloJednaci) {
		requestBody.append('naseCisloJednaci', data.naseCisloJednaci);
	}

	if (data.naseSpisovaZnacka) {
		requestBody.append('naseSpisovaZnacka', data.naseSpisovaZnacka);
	}

	if (data.vaseCisloJednaci) {
		requestBody.append('vaseCisloJednaci', data.vaseCisloJednaci);
	}

	if (data.vaseSpisovaZnacka) {
		requestBody.append('vaseSpisovaZnacka', data.vaseSpisovaZnacka);
	}

	data.documents?.forEach((documentId) =>
		requestBody.append('prilohyZDokumentu[]', documentId.toString())
	);

	data.files?.forEach((file) =>
		requestBody.append('soubory[]', file.fileId ?? '')
	);

	return requestBody;
};
